<template>
  <div class="content-wrapper">
    <section class="content-header">
      <h1>Nudge Console</h1>
      <ol class="breadcrumb">
        <li><a href="#"><i class="fa fa-dashboard"></i> Home</a></li>
        <li><router-link to="/nudge-console/dashboard">Dashboard</router-link></li>
        <li class="active">Edit campaign</li>
      </ol>
    </section>

    <section class="content nc-page">
      <div v-if="loadError" class="nc-surface nc-empty">
        <div class="nc-empty-icon"><i class="fa fa-exclamation-triangle"></i></div>
        <div class="nc-empty-title">Couldn't open this campaign</div>
        <div class="nc-empty-text">
          {{ loadError }}
          <br />
          <router-link to="/nudge-console/dashboard">Back to the Dashboard</router-link>
        </div>
      </div>

      <template v-else-if="raw">
        <div class="ec-topbar">
          <router-link class="nc-btn nc-btn-ghost nc-btn-sm" to="/nudge-console/dashboard">← Back</router-link>
          <h2 class="ec-topbar-title">Edit campaign — {{ raw.opportunity_name }}</h2>
        </div>

        <!-- One sheet, not a stack of cards: the left rail and the content column are two
             halves of the same decision, and separating them into their own surfaces made
             the page read as two unrelated forms. -->
        <div class="ec-sheet">
          <div class="ec-sheet-head">
            <div class="ec-avatar"><i class="fa fa-commenting-o"></i></div>
            <div class="ec-head-main">
              <div class="ec-head-line">
                <span class="ec-head-name">{{ customerName }} — {{ raw.opportunity_name }}</span>
                <span class="nc-badge" :class="statusBadgeClass">{{ statusLabel }}</span>
                <span class="nc-badge nc-badge-teal">{{ countryName }}</span>
              </div>
              <div class="ec-head-sub">
                {{ typeText }} · {{ sessionText }}
                <span v-if="triggerText"> · {{ triggerText }}</span>
              </div>
              <div class="ec-head-tags">
                <span class="ec-tag">{{ journeyName }}</span>
                <span class="ec-tag">{{ languageCountText }}</span>
                <span class="ec-tag" :class="{ 'ec-tag-ok': hasTemplate }">
                  <i v-if="hasTemplate" class="fa fa-check"></i>
                  {{ hasTemplate ? 'Approved template' : 'No template' }}
                </span>
              </div>
            </div>
          </div>

          <div class="ec-body">
            <!-- ── Left rail: everything about WHO and WHEN ─────────────── -->
            <aside class="ec-rail">
              <h4 class="ec-rail-title">Campaign</h4>
              <div class="ec-rail-row">
                <label class="ec-rail-label" for="ec-name">Name</label>
                <input id="ec-name" v-model="form.name" type="text" class="nc-input ec-rail-input" maxlength="200" />
              </div>

              <h4 class="ec-rail-title">{{ isBatch ? 'Schedule' : 'Trigger' }}</h4>

              <!-- Batch OOS has no trigger at all: a fixed audience sent once. start_date is
                   the only field the schema models for that. -->
              <template v-if="isBatch">
                <div class="ec-rail-row">
                  <label class="ec-rail-label" for="ec-deploy-date">Deploy on</label>
                  <input id="ec-deploy-date" v-model="form.deployDate" type="date" class="nc-input ec-rail-input" />
                </div>
                <div class="ec-rail-row">
                  <label class="ec-rail-label" for="ec-deploy-time">At</label>
                  <input id="ec-deploy-time" v-model="form.deployTime" type="time" class="nc-input ec-rail-input" />
                </div>
                <p class="ec-rail-note">
                  Your timezone ({{ localZone }}), not the campaign country's — the schema stores
                  one instant and nothing resolves a per-country timezone on this path yet.
                </p>
              </template>

              <!-- In-session: stored as HH:MM, same bounds the /opportunity form enforces. -->
              <template v-else-if="raw.is_in_session">
                <div class="ec-rail-row">
                  <label class="ec-rail-label" for="ec-hours">Hours</label>
                  <input id="ec-hours" v-model="form.sendHours" type="number" min="0" max="23" class="nc-input ec-rail-input" />
                </div>
                <div class="ec-rail-row">
                  <label class="ec-rail-label" for="ec-minutes">Minutes</label>
                  <input id="ec-minutes" v-model="form.sendMinutes" type="number" min="0" max="59" class="nc-input ec-rail-input" />
                </div>
                <p class="ec-rail-note">
                  Silence inside a live conversation before the nudge fires. Up to 23 hours;
                  0:00 fires immediately.
                </p>
              </template>

              <!-- Out-of-session: stored as h:<n> or d:<n>. -->
              <template v-else>
                <div class="ec-rail-row">
                  <label class="ec-rail-label" for="ec-window">Inactive for</label>
                  <input id="ec-window" v-model="form.windowValue" type="number" min="0" class="nc-input ec-rail-input" />
                </div>
                <div class="ec-rail-row">
                  <label class="ec-rail-label" for="ec-window-unit">Unit</label>
                  <nv-select id="ec-window-unit" v-model="form.windowUnit" class="nc-input ec-rail-input">
                    <option value="hrs">hours</option>
                    <option value="days">days</option>
                  </nv-select>
                </div>
                <p class="ec-rail-note">
                  Minimum 24 hours, or 1 day. 0 fires as soon as the user goes inactive — some
                  older campaigns are stored that way.
                </p>
              </template>

              <!-- Both columns are nulled for in-session campaigns in the real schema, so the
                   block isn't offered there rather than showing fields that can't be saved. -->
              <template v-if="!raw.is_in_session && !isBatch">
                <h4 class="ec-rail-title">Frequency</h4>
                <div class="ec-rail-row">
                  <label class="ec-rail-label" for="ec-max-nudges">Max per user</label>
                  <input id="ec-max-nudges" v-model="form.maxNudges" type="number" min="1" class="nc-input ec-rail-input" />
                </div>
                <div class="ec-rail-row">
                  <label class="ec-rail-label" for="ec-cooldown">Cooldown</label>
                  <input id="ec-cooldown" v-model="form.cooldownDays" type="number" min="0" class="nc-input ec-rail-input" />
                </div>
                <p class="ec-rail-note">Cooldown is in days — how long before this campaign may nudge the same user again.</p>
              </template>

              <h4 class="ec-rail-title">Set in the wizard</h4>
              <div class="ec-rail-fact">
                <span class="ec-rail-fact-label">Country</span><span class="ec-rail-fact-value">{{ countryName }}</span>
              </div>
              <div class="ec-rail-fact">
                <span class="ec-rail-fact-label">Customer</span><span class="ec-rail-fact-value">{{ customerName }}</span>
              </div>
              <div class="ec-rail-fact">
                <span class="ec-rail-fact-label">Journey</span><span class="ec-rail-fact-value">{{ journeyName }}</span>
              </div>
              <div class="ec-rail-fact">
                <span class="ec-rail-fact-label">Window kind</span><span class="ec-rail-fact-value">{{ sessionText }}</span>
              </div>
              <p class="ec-rail-note">
                Changing any of these changes what the rest of the campaign is allowed to
                contain — the journey decides the language list, the keywords and the template
                all at once. Rebuild it in
                <router-link to="/nudge-console/create-campaign">Create Campaign</router-link>.
              </p>
            </aside>

            <!-- ── Right column: everything about WHAT IT SAYS ──────────── -->
            <div class="ec-main">
              <div v-if="errors.length" class="nc-notice nc-notice-warn ec-errors">
                <i class="fa fa-exclamation-triangle"></i>
                <span>
                  <strong>{{ errors.length }} thing{{ errors.length === 1 ? '' : 's' }} to fix before saving:</strong>
                  <span v-for="(message, i) in errors" :key="i" class="ec-error-item">{{ message }}</span>
                </span>
              </div>
              <!-- Problems with the template itself (set up in Nudge Template Manager or Media
                   Management), which this page can't fix and shouldn't block a save over. -->
              <div v-if="templateWarnings.length" class="nc-notice nc-notice-warn ec-errors">
                <i class="fa fa-info-circle"></i>
                <span>
                  <strong>Template setup to check — sends will fail until it's fixed:</strong>
                  <span v-for="(message, i) in templateWarnings" :key="`w${i}`" class="ec-error-item">{{ message }}</span>
                </span>
              </div>

              <div v-for="(group, gi) in form.groups" :key="group.key" class="ec-group">
                <div class="ec-main-head">
                  <h4 class="ec-main-title">
                    Nudge content
                    <span class="ec-main-title-sep">—</span>
                    {{ variantLabel(gi) }}
                  </h4>
                  <div class="ec-main-head-actions">
                    <div v-if="form.groups.length > 1" class="ec-split">
                      <label :for="`ec-split-${gi}`">Traffic</label>
                      <input :id="`ec-split-${gi}`" v-model="group.splitPercent" type="number" min="1" max="99" class="nc-input ec-split-input" />
                      <span class="ec-split-unit">%</span>
                    </div>
                    <button
                      v-if="group.messages.length > 1"
                      class="nc-btn nc-btn-ghost nc-btn-sm"
                      @click="toggleAll(group)"
                    >
                      <i class="fa" :class="allOpen(group) ? 'fa-compress' : 'fa-th-list'"></i>
                      {{ allOpen(group) ? 'Collapse all' : 'View / edit all languages' }}
                    </button>
                    <button
                      class="nc-btn nc-btn-sm"
                      :class="previewOn(group) ? 'nc-btn-primary' : 'nc-btn-ghost'"
                      @click="togglePreview(group)"
                    >
                      <i class="fa fa-whatsapp"></i>
                      {{ previewOn(group) ? 'Hide preview' : 'WhatsApp preview' }}
                    </button>
                  </div>
                </div>

                <div class="ec-template-line">
                  <span class="ec-template-label">Template</span>
                  <span class="ec-template-value">{{ templateName(group.templateId) }}</span>
                </div>

                <!-- The template's media header. The file is set on the TEMPLATE, so changing
                     it here changes it for every campaign using that template — the picker
                     says how many, and the server only offers files that cover all of them. -->
                <div v-if="groupMedia(group)" class="ec-media">
                  <div class="ec-media-head">
                    <span class="ec-template-label">Header {{ groupMedia(group).label.toLowerCase() }}</span>
                    <span v-if="mediaRows(group).length < 2" class="ec-template-value">{{ groupMedia(group).mediaName || 'No file set' }}</span>
                    <button
                      class="nc-btn nc-btn-ghost nc-btn-sm"
                      :disabled="mediaPickerFor(group).loading"
                      @click="toggleMediaPicker(group)"
                    >
                      <i class="fa" :class="isPickerOpenFor(group, null) ? 'fa-times' : 'fa-exchange'"></i>
                      {{ isPickerOpenFor(group, null) ? 'Close' : (mediaRows(group).length > 1 ? 'Change for all languages' : 'Change media') }}
                    </button>
                  </div>
                  <!-- One language: the file itself. Several: one row per language, each with
                       its own file and its own Change (each language is its own mapping row). -->
                  <div v-if="mediaRows(group).length < 2" class="ec-media-showcase">
                    <media-preview
                      :format="groupMedia(group).format"
                      :media-id="groupMedia(group).mediaId"
                      :media-name="groupMedia(group).mediaName || ''"
                      :file-name="groupMedia(group).fileName || ''"
                      :country-id="raw.country_id"
                      :language-id="groupMedia(group).languageId"
                    />
                  </div>
                  <div v-else class="ec-media-langs">
                    <div v-for="row in mediaRows(group)" :key="row.language_id" class="ec-media-lang">
                      <span class="ec-var-label">{{ row.language }}</span>
                      <div class="ec-media-lang-preview">
                        <media-preview
                          compact
                          :show-caption="false"
                          :format="row.turn_template_data.HEADER_FORMAT"
                          :media-id="row.media_id"
                          :media-name="row.media_name || ''"
                          :file-name="row.media_file_name || ''"
                          :country-id="raw.country_id"
                          :language-id="row.language_id"
                          missing-text=": no file set"
                        />
                      </div>
                      <span class="ec-media-lang-name">{{ row.media_name || 'No file set' }}</span>
                      <button
                        class="nc-btn nc-btn-ghost nc-btn-sm"
                        :disabled="mediaPickerFor(group).loading"
                        @click="toggleMediaPicker(group, row)"
                      >
                        <i class="fa" :class="isPickerOpenFor(group, row) ? 'fa-times' : 'fa-exchange'"></i>
                        {{ isPickerOpenFor(group, row) ? 'Close' : 'Change' }}
                      </button>
                    </div>
                  </div>
                  <p v-if="groupMedia(group).problem" class="ec-media-problem">
                    <i class="fa fa-exclamation-triangle"></i> {{ groupMedia(group).problem }}
                  </p>

                  <!-- A PDF's file name is shown to the person in WhatsApp, so it's reviewed
                       and editable here, per language. It's the file's own name in Media
                       Management for this country, saved straight away like "Change media". -->
                  <div v-if="documentRows(group).length" class="ec-vars ec-filenames">
                    <div class="ec-vars-title">
                      PDF name in chat
                      <span class="ec-vars-sub">what the person sees in WhatsApp, per language</span>
                    </div>
                    <div v-for="row in documentRows(group)" :key="row.language_id" class="ec-var-row">
                      <span class="ec-var-label">{{ row.language }}</span>
                      <input
                        v-model="fileNameDrafts[fileNameKey(group, row)]"
                        type="text"
                        class="nc-input ec-var-text"
                        maxlength="100"
                        placeholder="e.g. Clinic visit guide.pdf"
                        :aria-label="`${row.language} file name`"
                      />
                    </div>
                    <div class="ec-filenames-foot">
                      <span class="ec-var-hint">".pdf" is added if left off. Changes this file's name for every template using it, as soon as you save it.</span>
                      <button class="nc-btn nc-btn-ghost nc-btn-sm" :disabled="saving || !fileNamesChanged(group)" @click="saveFileNames(group)">
                        <i class="fa fa-check"></i> Save names
                      </button>
                    </div>
                  </div>

                  <div v-if="mediaPickerFor(group).open" class="ec-media-picker">
                    <p v-if="mediaPickerFor(group).loading" class="ec-main-note"><i class="fa fa-spinner fa-spin"></i> Loading Media Management…</p>
                    <p v-else-if="mediaPickerFor(group).error" class="ec-media-problem">{{ mediaPickerFor(group).error }}</p>
                    <template v-else-if="mediaPickerFor(group).info">
                      <p class="ec-media-target">
                        Changing the {{ mediaPickerFor(group).info.label.toLowerCase() }} for
                        <strong>{{ mediaPickerFor(group).languageName || 'every language' }}</strong>
                      </p>
                      <p class="ec-main-note">
                        Applies to the template, so it changes the header for
                        <strong>{{ mediaPickerFor(group).info.campaigns }} campaign{{ mediaPickerFor(group).info.campaigns === 1 ? '' : 's' }}</strong>
                        using it, as soon as you apply it (not on Save). Only {{ mediaPickerFor(group).info.label.toLowerCase() }}s
                        with a file in every language ({{ mediaPickerFor(group).info.languages.join(', ') }}) for
                        {{ mediaPickerFor(group).info.countries.join(', ') || 'these countries' }} are listed.
                        Upload new files in <router-link to="/media-management/view">Media Management</router-link>.
                      </p>
                      <p v-if="mediaPickerFor(group).info.skippedCountries.length" class="ec-main-note">
                        Not checked for {{ mediaPickerFor(group).info.skippedCountries.join(', ') }}: no WhatsApp account there.
                      </p>
                      <p v-if="!mediaPickerFor(group).info.options.length" class="ec-media-problem">
                        Nothing in Media Management fits. Upload {{ mediaPickerFor(group).info.hint }} for those languages first.
                      </p>
                      <label
                        v-for="option in mediaPickerFor(group).info.options"
                        :key="option.id"
                        class="ec-media-option"
                        :class="{ 'is-current': mediaPickerFor(group).info.currentMediaIds.indexOf(option.id) !== -1 }"
                      >
                        <input v-model="mediaPickerFor(group).selected" type="radio" :value="option.id" />
                        <span class="ec-media-option-thumb">
                          <media-preview
                            compact
                            :show-caption="false"
                            :format="option.format || mediaPickerFor(group).info.headerType"
                            :media-id="option.id"
                            :media-name="option.name"
                            :file-name="option.fileName || ''"
                            :country-id="raw.country_id"
                            :language-id="mediaPickerFor(group).languageId"
                          />
                        </span>
                        <span class="ec-media-option-name">{{ option.name }}</span>
                        <span class="ec-media-option-meta">{{ option.format }}<span v-if="option.fileName"> · {{ option.fileName }}</span></span>
                        <span v-if="mediaPickerFor(group).info.currentMediaIds.indexOf(option.id) !== -1" class="nc-badge nc-badge-teal">Current</span>
                      </label>
                      <button
                        v-if="mediaPickerFor(group).info.options.length"
                        class="nc-btn nc-btn-primary nc-btn-sm"
                        :disabled="!mediaPickerFor(group).selected || mediaPickerFor(group).info.currentMediaIds.indexOf(mediaPickerFor(group).selected) !== -1"
                        @click="applyMedia(group)"
                      >
                        <i class="fa fa-check"></i> Use this file
                      </button>
                    </template>
                  </div>
                </div>

                <p v-if="!group.messages.length" class="ec-main-note">
                  This group has no message rows stored, so there's nothing to edit here.
                </p>

                <!-- One collapsible block per language. Collapsed shows the first line, which
                     is what you scan when checking a translation landed; open shows the whole
                     body. A language with a problem is forced open by validate(). -->
                <div
                  v-for="message in group.messages"
                  :key="`${group.key}-${message.lang_id}`"
                  class="ec-lang"
                  :class="{ 'is-open': isOpen(group, message), 'is-flagged': isFlagged(message) }"
                >
                  <button class="ec-lang-head" :aria-expanded="isOpen(group, message) ? 'true' : 'false'" @click="toggle(group, message)">
                    <!-- The ISO alias, not a flag: a language is not a country, and picking one
                         flag per language gets it wrong for every language spoken in more than
                         one place. -->
                    <span class="ec-lang-code">{{ message.language }}</span>
                    <span class="ec-lang-name">{{ languageName(message.language) }}</span>
                    <span class="ec-lang-preview">{{ previewOf(message) }}</span>
                    <i class="fa fa-caret-right ec-lang-caret"></i>
                  </button>
                  <div v-if="isOpen(group, message)" class="ec-lang-body" :class="{ 'has-preview': previewOn(group) }">
                    <div class="ec-editor">
                      <textarea
                        v-model="message.message"
                        class="nc-input ec-textarea"
                        rows="4"
                        :aria-label="`${languageName(message.language)} message`"
                      ></textarea>
                      <div class="ec-lang-foot">
                        <span class="ec-count" :class="{ 'is-over': lengthOf(message) > maxBodyChars }">
                          {{ lengthOf(message) }} / {{ maxBodyChars }} chars
                        </span>
                        <span v-if="lengthOf(message) > maxBodyChars" class="ec-count-warn">
                          Over the WhatsApp body limit — trim it before saving.
                        </span>
                        <span v-else-if="!lengthOf(message)" class="ec-count-warn">
                          Empty — this language won't be sent.
                        </span>
                      </div>

                      <!-- What fills the approved template's {{n}} slots when this language is
                           sent — saved as the group's dynamic_value_configuration, the shape the
                           /opportunity form writes. -->
                      <div v-if="slotsFor(group, message).length" class="ec-vars">
                        <div class="ec-vars-title">
                          Template values
                          <span class="ec-vars-sub">what fills each <span v-pre>{{n}}</span> when this is sent</span>
                        </div>
                        <p v-if="!variablesFor(group, message).dynamic" class="ec-media-problem">
                          <i class="fa fa-exclamation-triangle"></i>
                          This template has variables but isn't marked Dynamic in Nudge Template Manager, so the
                          sender won't fill them and WhatsApp will reject the send. Turn on "Dynamic" there first.
                        </p>
                        <div v-for="slot in slotsFor(group, message)" :key="slot.key" class="ec-var-row">
                          <span class="ec-var-label">{{ slot.label }}</span>
                          <nv-select v-model="slot.entry.source" class="nc-input ec-var-source" :aria-label="`${slot.label} source`">
                            <option value="">Choose…</option>
                            <option v-for="option in variableSources" :key="option.value" :value="option.value">{{ option.label }}</option>
                          </nv-select>
                          <input
                            v-if="slot.entry.source === 'text'"
                            v-model="slot.entry.text"
                            type="text"
                            class="nc-input ec-var-text"
                            :placeholder="`Text in ${languageName(message.language)}`"
                            :aria-label="`${slot.label} text`"
                          />
                          <span v-else-if="slot.entry.source" class="ec-var-hint">Filled per user by the sender</span>
                        </div>
                      </div>
                    </div>

                    <!-- Rendered from the live Turn template (getNudgePreview) when the group
                         has one, because that approved body is literally what WhatsApp sends
                         for a template message — not from the textarea, which is the stored
                         campaign text. Without a template it falls back to the textarea and
                         says so. -->
                    <div v-if="previewOn(group)" class="ec-preview">
                      <div class="ec-phone">
                        <div class="ec-bubble">
                          <template v-if="previewFor(group, message)">
                            <media-preview
                              v-if="previewFor(group, message).media"
                              :format="previewFor(group, message).media.format"
                              :media-id="previewFor(group, message).media.mediaId"
                              :media-name="previewFor(group, message).media.mediaName"
                              :file-name="previewFor(group, message).media.fileName"
                              :country-id="raw.country_id"
                              :language-id="previewFor(group, message).media.languageId"
                            />
                            <div v-if="previewFor(group, message).header" class="ec-bubble-header">
                              <span v-for="(seg, si) in previewFor(group, message).header.parts" :key="`h${si}`">
                                <span>{{ seg.text }}</span>
                                <span class="ec-var" :class="{ 'is-unset': !seg.value }">{{ seg.value || seg.token }}</span>
                              </span>
                              <span>{{ previewFor(group, message).header.last }}</span>
                            </div>
                            <div class="ec-bubble-body">
                              <span v-for="(seg, si) in previewFor(group, message).body.parts" :key="`b${si}`">
                                <span>{{ seg.text }}</span>
                                <span class="ec-var" :class="{ 'is-unset': !seg.value }">{{ seg.value || seg.token }}</span>
                              </span>
                              <span>{{ previewFor(group, message).body.last }}</span>
                            </div>
                            <div v-if="previewFor(group, message).footer" class="ec-bubble-footer">
                              {{ previewFor(group, message).footer }}
                            </div>
                          </template>
                          <div v-else class="ec-bubble-body">{{ message.message || 'No message set' }}</div>
                          <div class="ec-bubble-meta">{{ nowTime }} <i class="fa fa-check"></i><i class="fa fa-check"></i></div>
                        </div>
                        <div
                          v-for="(button, bi) in previewButtons(group, message)"
                          :key="`btn${bi}`"
                          class="ec-bubble-button"
                        >
                          <i class="fa fa-reply"></i> {{ button }}
                        </div>
                      </div>
                      <p class="ec-preview-note">{{ previewNote(group, message) }}</p>
                    </div>
                  </div>
                </div>

                <h5 class="ec-sub-title">Where a reply lands</h5>
                <div v-for="(destination, di) in group.destinations" :key="`${group.key}-dest-${di}`" class="ec-dest">
                  <!-- Website links were never wired into the Nudge Console destination step,
                       so one on a hand-built campaign is shown and written back unchanged
                       rather than edited or, worse, silently dropped on save. -->
                  <template v-if="destination.type === 'website_link'">
                    <div class="ec-dest-locked">
                      <span class="ec-rail-fact-label">Website link</span>
                      <span class="ec-rail-fact-value">
                        Link #{{ destination.websiteLink }} — kept as-is, edit website links in Database Marketing
                      </span>
                    </div>
                  </template>
                  <template v-else>
                    <nv-select v-model="destination.type" class="nc-input ec-dest-type" aria-label="Destination type">
                      <option value="keyword">Keyword</option>
                      <option value="destination_conversation">Conversation</option>
                    </nv-select>
                    <nv-select
                      v-if="destination.type === 'keyword'"
                      v-model="destination.keywordId"
                      class="nc-input ec-dest-target"
                      aria-label="Keyword"
                    >
                      <option :value="null">Choose a keyword…</option>
                      <option v-for="keyword in keywordList" :key="keyword.id" :value="keyword.id">{{ keyword.keyword }}</option>
                    </nv-select>
                    <nv-select v-else v-model="destination.conversationId" class="nc-input ec-dest-target" aria-label="Conversation">
                      <option :value="null">Choose a conversation…</option>
                      <option v-for="app in conversationList" :key="app.id" :value="app.id">{{ app.appName }}</option>
                    </nv-select>
                    <button
                      v-if="group.destinations.length > 1"
                      class="nc-btn nc-btn-sm nc-btn-danger"
                      title="Remove this destination"
                      @click="group.destinations.splice(di, 1)"
                    >
                      <i class="fa fa-trash-o"></i>
                    </button>
                  </template>
                </div>
                <button class="nc-btn nc-btn-ghost nc-btn-sm ec-add-dest" @click="addDestination(group)">
                  <i class="fa fa-plus"></i> Add a destination
                </button>
              </div>

              <!-- Present but disabled, the same way the wizard's template step shows its own
                   unbuilt option. A second variant needs its own approved template (the wizard
                   has a whole step for it), and updateOpportunity can only upsert groups, never
                   delete one — so a variant added here could never be taken away again. -->
              <button
                v-if="form.groups.length < 2"
                class="nc-btn nc-btn-ghost nc-btn-sm ec-add-variant"
                disabled
                title="Variant B needs its own approved template — add it in Create Campaign"
              >
                <i class="fa fa-plus"></i> Add Variant B (in Create Campaign only)
              </button>
            </div>
          </div>

          <div class="ec-footer">
            <div class="ec-footer-facts">
              <span><i class="fa fa-language"></i> {{ languageCountText }}</span>
              <span><i class="fa fa-flask"></i> {{ variantCountText }}</span>
            </div>
            <div class="ec-footer-actions">
              <router-link class="nc-btn nc-btn-ghost" to="/nudge-console/dashboard">Cancel</router-link>
              <button class="nc-btn nc-btn-ghost" :disabled="saving" @click="save()">
                <i class="fa fa-check"></i> Save changes
              </button>
              <!-- Only for a campaign that isn't already running. This writes the same single
                   column the Dashboard's Activate toggle writes, so the two can't mean
                   different things by "active". -->
              <button
                v-if="raw.opportunity_status !== activeStatus"
                class="nc-btn nc-btn-primary"
                :disabled="saving"
                @click="save(true)"
              >
                <i class="fa fa-paper-plane-o"></i> Save &amp; activate
              </button>
            </div>
          </div>
        </div>
      </template>
    </section>

    <BlockUI v-if="loading || saving"><i class="fa fa-cog fa-spin fa-3x fa-fw"></i><br />Please wait...</BlockUI>
  </div>
</template>

<script>
import api from '../../../utils/api'
import { languages as LANGUAGES } from '../../../config/constants'
import MediaPreview from '../common/MediaPreview.vue'
import {
  CAMPAIGN_STATUS,
  STATUS_LABELS,
  STATUS_BADGE_CLASSES,
  describeTrigger,
  describeSession,
  describeCampaignType,
  isoOf
} from './constants'

const LANGUAGE_NAMES = LANGUAGES.reduce((map, lang) => {
  map[lang.value] = lang.name
  return map
}, {})

// The WhatsApp template body limit, enforced by createOrUpdateOpportunity.vue's own preview
// check ("Body length should not exceed the total limit of 1024 characters"). Same number
// here so the two pages can't disagree about what fits.
const MAX_BODY_CHARS = 1024

// How much of a message the collapsed row shows. Long enough to tell two translations apart,
// short enough to stay on one line at rail width.
const PREVIEW_CHARS = 90

// Turn/WhatsApp template variables, e.g. {{1}}. Same expression createOrUpdateOpportunity.vue
// splits headers and bodies on, so both pages agree about what counts as a placeholder.
const TEMPLATE_VARIABLE = /\{\{.*?\}\}/g

const pad = n => String(n).padStart(2, '0')

// Splits an approved template's text around its {{n}} placeholders and pairs each gap with
// the value stored for it, so the preview can render the filled text as plain nodes. Built
// as segments rather than one interpolated string because an unset variable has to look
// different from a filled one — and because interpolating into markup would mean v-html.
function segmentsFor (content, values) {
  const text = String(content == null ? '' : content)
  const tokens = text.match(TEMPLATE_VARIABLE) || []
  const parts = text.split(TEMPLATE_VARIABLE)
  const last = parts.pop()
  return {
    parts: parts.map((part, i) => ({
      text: part,
      token: tokens[i] || '{{?}}',
      value: (values && values[slotIndex(tokens[i], i)]) || ''
    })),
    last
  }
}

// What can fill a template {{n}} at send time: the two per-user tokens the sender resolves
// (the /opportunity form's own help text lists them) or fixed text. Same set the Create
// Campaign chat offers (server templateVariables.js).
const VARIABLE_SOURCES = [
  { value: 'user_name', label: 'User name', token: '{{USER_NAME}}', display: '[user name]' },
  { value: 'provider_code', label: 'Provider code', token: '{{PROVIDER_CODE}}', display: '[provider code]' },
  { value: 'text', label: 'Fixed text' }
]

const MEDIA_FORMATS = { IMAGE: 'Image', VIDEO: 'Video', DOCUMENT: 'Document' }

// WhatsApp's header-text limit; the body limit is MAX_BODY_CHARS. A per-user value's length
// isn't known until send time, so it's budgeted at this many characters.
const MAX_HEADER_CHARS = 60
const TOKEN_LENGTH_BUDGET = 30

// Which stored value fills a token: values[n - 1] for {{n}} (the send passes values as
// parameters 1..n, by number); a non-numeric token falls back to its position.
function slotIndex (token, position) {
  const match = /^\{\{\s*(\d+)\s*\}\}$/.exec(token || '')
  return match ? Number(match[1]) - 1 : position
}

// Slots a text needs: the highest {{n}}, or the token count when they aren't numbered.
function countSlots (text) {
  const tokens = String(text || '').match(TEMPLATE_VARIABLE) || []
  const numbers = tokens.map(t => slotIndex(t, -1) + 1).filter(n => n > 0)
  return numbers.length === tokens.length && numbers.length ? Math.max.apply(null, numbers) : tokens.length
}

// A stored value back into the editor's { source, text }. Only an exact token counts as one;
// anything else (including text with a token inside, which the /opportunity form allows) is
// kept as fixed text, so saving never rewrites it.
function entryFromValue (value) {
  const source = VARIABLE_SOURCES.find(s => s.token && s.token === value)
  if (source) return { source: source.value, text: '' }
  if (value) return { source: 'text', text: String(value) }
  return { source: '', text: '' }
}

function valueFromEntry (entry) {
  const source = VARIABLE_SOURCES.find(s => s.value === entry.source)
  if (!source) return ''
  return source.token || String(entry.text || '').trim()
}

// Meta's rule for a parameter value: not empty, no line breaks or tabs, at most 4 spaces in
// a row. Braces would read as another variable.
function paramProblem (text) {
  const value = String(text || '')
  if (!value.trim()) return 'is empty'
  if (/[\n\r\t]/.test(value)) return "can't have line breaks or tabs"
  if (/ {5,}/.test(value)) return "can't have more than 4 spaces in a row"
  // The sender's own tokens may sit inside fixed text ("Hi {{USER_NAME}}"), as the
  // /opportunity form allows; any other brace would read as a variable.
  if (/[{}]/.test(value.replace(/\{\{(USER_NAME|PROVIDER_CODE)\}\}/g, ''))) return "can't have { or } (other than {{USER_NAME}} or {{PROVIDER_CODE}})"
  return null
}

function parseConfig (config) {
  if (!config) return {}
  if (typeof config === 'string') {
    try { return JSON.parse(config) || {} } catch (err) { return {} }
  }
  return config
}

// Which of the three destination columns a stored opportunity_group_options row uses. The
// fetch aliases link_id to website_link and computes its own destinationToUse, but a row
// written before that alias existed can arrive without it — derive it from the columns
// rather than trusting the label.
function destinationTypeOf (row) {
  if (row.keyword_id) return 'keyword'
  if (row.destination_conversation_id) return 'destination_conversation'
  if (row.website_link) return 'website_link'
  return 'keyword'
}

export default {
  name: 'nudge-edit-campaign',
  components: { MediaPreview },
  data () {
    return {
      variableSources: VARIABLE_SOURCES,
      // Media picker state per group key: { open, loading, error, info, selected }.
      mediaPickers: {},
      // PDF header names being edited, keyed `${groupKey}-${languageId}`.
      fileNameDrafts: {},
      loading: false,
      saving: false,
      loadError: '',
      campaignId: null,
      // The opportunity row exactly as getOpportunityDetails returned it. Everything this
      // page doesn't edit is spread back out of here on save, so a column this form has
      // never heard of can't be blanked by saving.
      raw: null,
      keywordList: [],
      conversationList: [],
      templates: [],
      // getOpportunityDetails returns `opportunities.*` and nothing else, so the country and
      // customer arrive as bare ids — these two lists turn them back into names.
      countries: [],
      customers: [],
      errors: [],
      // Which language blocks are open, keyed `${groupKey}-${lang_id}`. Every key is seeded
      // in hydrate() so later writes are plain assignments — Vue 2 wouldn't make a key added
      // after the fact reactive without $set.
      expanded: {},
      // Languages validate() called out on the last attempt, same key shape. Drives the
      // flagged border, and they're force-opened so a problem is never hidden in a collapsed
      // block.
      flagged: {},
      maxBodyChars: MAX_BODY_CHARS,
      activeStatus: CAMPAIGN_STATUS.active,
      // Which groups are showing the WhatsApp preview, keyed by group key.
      previewShown: {},
      // Approved-template rows from getNudgePreview, keyed by template NAME (that endpoint
      // takes a name, not an id). Fetched lazily on first preview: it hits the live Turn API,
      // so every page open shouldn't pay for it.
      previewsByTemplate: {},
      previewErrors: {},
      form: {
        name: '',
        windowValue: '',
        windowUnit: 'days',
        sendHours: '0',
        sendMinutes: '0',
        maxNudges: '',
        cooldownDays: '',
        deployDate: '',
        deployTime: '',
        groups: []
      }
    }
  },
  computed: {
    // Mirrors the Dashboard's own dispatch-type read: no inactivity timing means a fixed
    // audience and a single send, not a trigger.
    isBatch () {
      return !this.raw.lower_inactivity_timing
    },
    typeText () {
      return describeCampaignType(this.raw).label
    },
    sessionText () {
      return describeSession(this.raw)
    },
    triggerText () {
      return this.isBatch ? '' : describeTrigger(this.raw)
    },
    statusLabel () {
      return STATUS_LABELS[this.raw.opportunity_status] || 'Unknown'
    },
    statusBadgeClass () {
      return STATUS_BADGE_CLASSES[this.raw.opportunity_status] || 'nc-badge-neutral'
    },
    countryName () {
      const match = this.countries.find(c => c.country_id === this.raw.country_id)
      return (match && match.country) || `Country #${this.raw.country_id}`
    },
    customerName () {
      const match = this.customers.find(c => c.id === this.raw.customer_id)
      return (match && match.name) || `Customer #${this.raw.customer_id}`
    },
    journeyName () {
      const app = this.conversationList.find(a => a.id === this.raw.conversation_id)
      return app ? app.appName : `Journey #${this.raw.conversation_id}`
    },
    // Counted across groups rather than summed: an A/B campaign says the same thing in the
    // same languages twice, so summing would double it.
    languageCount () {
      const seen = new Set()
      this.form.groups.forEach(group => group.messages.forEach(m => seen.add(m.lang_id)))
      return seen.size
    },
    languageCountText () {
      return `${this.languageCount} language${this.languageCount === 1 ? '' : 's'}`
    },
    variantCountText () {
      return this.form.groups.length > 1 ? `${this.form.groups.length} variants` : 'Single version'
    },
    hasTemplate () {
      return this.form.groups.some(group => group.templateId)
    },
    localZone () {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time'
      } catch (err) {
        return 'local time'
      }
    },
    // Chrome on the preview bubble, so it reads as a chat message rather than a text box.
    // Computed once per render rather than ticking — a clock that moves draws the eye away
    // from the copy, which is the thing being reviewed.
    nowTime () {
      const now = new Date()
      return `${pad(now.getHours())}:${pad(now.getMinutes())}`
    },
    // Template-side problems found in the loaded template rows: a media header with no file
    // (or none for this country), or variables on a template not marked Dynamic.
    templateWarnings () {
      const warnings = []
      this.form.groups.forEach((group, gi) => {
        const label = this.variantLabel(gi)
        const name = this.rawTemplateName(group.templateId)
        if (name && this.previewErrors[name]) {
          warnings.push(`${label}: couldn't load "${name}" from Turn, so its variable values and header weren't checked; saving keeps the stored values as they are. Open the WhatsApp preview to retry.`)
        }
        const media = this.groupMedia(group)
        if (media && media.problem) warnings.push(`${label}: ${media.problem}`)
        const notDynamic = group.messages
          .filter(message => this.slotsFor(group, message).length && !this.variablesFor(group, message).dynamic)
          .map(message => this.languageName(message.language))
        if (notDynamic.length) warnings.push(`${label}: the template has variables but isn't marked Dynamic for ${notDynamic.join(', ')} — turn on "Dynamic" in Nudge Template Manager.`)
        const unnamed = this.documentRows(group).filter(row => !row.media_file_name).map(row => row.language)
        if (unnamed.length) warnings.push(`${label}: the PDF has no file name for ${unnamed.join(', ')}, so WhatsApp shows a generic one. Set it under "PDF name in chat".`)
      })
      return warnings
    }
  },
  mounted () {
    this.campaignId = parseInt(this.$route.query.campaignId, 10)
    if (!Number.isFinite(this.campaignId)) {
      this.loadError = 'No campaign id was given.'
      return
    }
    this.load()
  },
  methods: {
    languageName (alias) {
      return LANGUAGE_NAMES[alias] || alias || 'Message'
    },

    variantLabel (index) {
      if (this.form.groups.length < 2) return this.form.groups[index].groupName || 'Single version'
      return `Variant ${String.fromCharCode(65 + index)}`
    },

    templateName (templateId) {
      if (!templateId) return 'None — free-written content'
      const match = this.templates.find(t => t.id === templateId)
      return match ? match.msg_template_name : `Template #${templateId}`
    },

    langKey (group, message) {
      return `${group.key}-${message.lang_id}`
    },

    isOpen (group, message) {
      return Boolean(this.expanded[this.langKey(group, message)])
    },

    isFlagged (message) {
      return Boolean(this.flagged[message.lang_id])
    },

    toggle (group, message) {
      const key = this.langKey(group, message)
      this.expanded[key] = !this.expanded[key]
    },

    allOpen (group) {
      return group.messages.every(message => this.isOpen(group, message))
    },

    toggleAll (group) {
      const open = !this.allOpen(group)
      group.messages.forEach(message => { this.expanded[this.langKey(group, message)] = open })
    },

    lengthOf (message) {
      return (message.message || '').length
    },

    previewOf (message) {
      // First line only: a body with its call to action on line three would otherwise push
      // the useful part of the preview off the end.
      const firstLine = String(message.message || '').split('\n')[0].trim()
      if (!firstLine) return 'No message set'
      return firstLine.length > PREVIEW_CHARS ? `${firstLine.slice(0, PREVIEW_CHARS)}…` : firstLine
    },

    addDestination (group) {
      group.destinations.push({ type: 'keyword', keywordId: null, conversationId: null, websiteLink: null })
    },

    // ── WhatsApp preview ─────────────────────────────────────────────────────

    previewOn (group) {
      return Boolean(this.previewShown[group.key])
    },

    togglePreview (group) {
      const on = !this.previewOn(group)
      this.$set(this.previewShown, group.key, on)
      // A load that failed earlier (Turn unreachable) is retried when the preview is opened.
      if (on) this.loadTemplatePreview(group, Boolean(this.previewErrors[this.rawTemplateName(group.templateId)]))
    },

    // getNudgePreview takes the template NAME, so this only works once reengagement/nudges has
    // resolved the id. A group with no template needs no fetch at all — its preview renders
    // the textarea.
    // Loaded for every template group on open, not only when the preview is toggled: the
    // rows also say which {{n}} slots need values and what media header the template has.
    // countryId makes the endpoint report whether the header file exists for this country.
    async loadTemplatePreview (group, force = false) {
      const name = this.rawTemplateName(group.templateId)
      if (!name || (this.previewsByTemplate[name] && !force)) return
      try {
        const { data, status } = await api.buildRequest('getNudgePreview', 'GET', { nudge: name, countryId: this.raw.country_id })
        if (status !== 200) throw new Error('Request failed')
        this.$delete(this.previewErrors, name)
        this.$set(this.previewsByTemplate, name, (data && data.payload) || [])
      } catch (err) {
        // A Turn outage costs the preview, never the editor — the message text is still
        // there to edit and save.
        this.$set(this.previewErrors, name, true)
        this.$set(this.previewsByTemplate, name, [])
      }
      this.form.groups.filter(g => this.rawTemplateName(g.templateId) === name).forEach(group => {
        this.initVariables(group)
        this.seedFileNames(group, force)
      })
    },

    // ── PDF header file names ────────────────────────────────────────────────

    // One row per campaign language whose template header is a PDF with a file set.
    documentRows (group) {
      const name = this.rawTemplateName(group.templateId)
      const rows = (name && this.previewsByTemplate[name]) || []
      const languages = group.messages.map(m => m.language)
      return rows.filter(row => row.turn_template_data && row.turn_template_data.HEADER_FORMAT === 'DOCUMENT' && row.media_id && languages.indexOf(row.lang) !== -1)
    },

    fileNameKey (group, row) {
      return `${group.key}-${row.language_id}`
    },

    // Fills the inputs from the stored names; `force` overwrites (after a save or a file change).
    seedFileNames (group, force = false) {
      this.documentRows(group).forEach(row => {
        const key = this.fileNameKey(group, row)
        if (force || this.fileNameDrafts[key] === undefined) this.$set(this.fileNameDrafts, key, row.media_file_name || '')
      })
    },

    fileNamesChanged (group) {
      return this.documentRows(group).some(row => (this.fileNameDrafts[this.fileNameKey(group, row)] || '') !== (row.media_file_name || ''))
    },

    async saveFileNames (group) {
      const rows = this.documentRows(group)
      const empty = rows.filter(row => !String(this.fileNameDrafts[this.fileNameKey(group, row)] || '').trim())
      if (empty.length) {
        this.$swal({ type: 'warning', text: `Give the PDF a name for ${empty.map(row => row.language).join(', ')} — WhatsApp shows it to the person.` })
        return
      }
      // Languages can point at different files; each file's names are saved on their own.
      const byMedia = {}
      rows.forEach(row => {
        byMedia[row.media_id] = byMedia[row.media_id] || {}
        byMedia[row.media_id][row.language_id] = this.fileNameDrafts[this.fileNameKey(group, row)]
      })
      this.saving = true
      try {
        for (const mediaId of Object.keys(byMedia)) {
          const { data, status } = await api.buildRequest('nudge-media-filename', 'POST', { mediaId, countryId: this.raw.country_id, names: byMedia[mediaId] })
          const payload = (data && data.payload) || {}
          if (status !== 200) throw new Error(payload.message || `HTTP ${status}`)
        }
        await this.loadTemplatePreview(group, true)
        this.$swal({ type: 'success', text: 'PDF name saved.' })
      } catch (err) {
        this.$swal({ type: 'error', text: `Couldn't save the PDF name: ${err.message}` })
      } finally {
        this.saving = false
      }
    },

    // ── Template variables ───────────────────────────────────────────────────

    // One { dynamic, header: [entry], body: [entry] } per language alias, sized to the
    // approved template's own {{n}} slots and seeded from the stored values. Built once per
    // language (an edit in progress isn't reset by a preview refresh).
    initVariables (group) {
      group.messages.forEach(message => {
        const row = this.templateRowFor(group, message)
        if (!row || group.variables[message.language]) return
        const data = row.turn_template_data || {}
        const stored = parseConfig(group.dynamicValueConfiguration)[message.language] || {}
        const textHeader = data.HEADER && (!data.HEADER_FORMAT || data.HEADER_FORMAT === 'TEXT') ? data.HEADER : ''
        const build = (count, values) => Array.from({ length: count }, (unused, i) => entryFromValue((values || [])[i]))
        this.$set(group.variables, message.language, {
          dynamic: Boolean(row.is_dynamic_template),
          header: build(countSlots(textHeader), stored.header),
          body: build(countSlots(String(data.BODY || '').trim()), stored.body)
        })
      })
    },

    variablesFor (group, message) {
      return group.variables[message.language] || { dynamic: false, header: [], body: [] }
    },

    slotsFor (group, message) {
      const vars = this.variablesFor(group, message)
      return [
        ...vars.header.map((entry, i) => ({ key: `h${i}`, label: `Header {{${i + 1}}}`, entry })),
        ...vars.body.map((entry, i) => ({ key: `b${i}`, label: `{{${i + 1}}}`, entry }))
      ]
    },

    // The values the preview bubble shows: the live editor entries when there are some,
    // otherwise whatever is stored.
    previewValues (group, message, component) {
      const vars = group.variables[message.language]
      if (vars) {
        return vars[component].map(entry => {
          const source = VARIABLE_SOURCES.find(s => s.value === entry.source)
          if (!source) return ''
          return source.display || entry.text
        })
      }
      return (parseConfig(group.dynamicValueConfiguration)[message.language] || {})[component]
    },

    // dynamic_value_configuration to save: what was stored, with each edited language
    // replaced. Only Dynamic templates get values — the send path rejects values on a
    // template that isn't flagged dynamic.
    dynamicConfigFor (group) {
      const config = JSON.parse(JSON.stringify(parseConfig(group.dynamicValueConfiguration)))
      Object.keys(group.variables).forEach(alias => {
        const vars = group.variables[alias]
        if (!vars.dynamic) return
        // A Dynamic template with no slots is left as stored: the send path rejects a
        // Dynamic template with no entry at all, so deleting one would break sends.
        if (!vars.header.length && !vars.body.length) return
        const entry = {}
        if (vars.header.length) entry.header = vars.header.map(valueFromEntry)
        if (vars.body.length) entry.body = vars.body.map(valueFromEntry)
        config[alias] = entry
      })
      return Object.keys(config).length ? config : null
    },

    // ── Media header ─────────────────────────────────────────────────────────

    // The group's media header, from the template rows: format, the file set for it, and any
    // reason it wouldn't send. Null when the template has no media header (or isn't loaded).
    groupMedia (group) {
      const name = this.rawTemplateName(group.templateId)
      const rows = (name && this.previewsByTemplate[name]) || []
      const languages = group.messages.map(m => m.language)
      const mediaRows = rows.filter(row => row.turn_template_data && MEDIA_FORMATS[row.turn_template_data.HEADER_FORMAT] && (!languages.length || languages.indexOf(row.lang) !== -1))
      if (!mediaRows.length) return null
      const first = mediaRows.find(row => row.media_id) || mediaRows[0]
      const format = first.turn_template_data.HEADER_FORMAT
      let problem = ''
      const noFile = mediaRows.filter(row => !row.media_id).map(row => row.language)
      const noCountryFile = mediaRows.filter(row => row.media_id && (!row.media_has_whatsapp || !row.media_has_file)).map(row => row.language)
      if (noFile.length) problem = `the ${MEDIA_FORMATS[format].toLowerCase()} header has no file set for ${noFile.join(', ')}. Pick one with "Change media".`
      else if (noCountryFile.length) problem = `"${first.media_name}" has no WhatsApp copy for ${this.countryName} in ${noCountryFile.join(', ')}, so those sends go out without their header. Pick another file or upload one for this country in Media Management.`
      return {
        format,
        label: MEDIA_FORMATS[format],
        mediaId: first.media_id || null,
        mediaName: first.media_name || '',
        fileName: first.media_file_name || '',
        languageId: first.language_id,
        problem
      }
    },

    // The campaign's languages whose template header is a media file, one row each.
    mediaRows (group) {
      const name = this.rawTemplateName(group.templateId)
      const rows = (name && this.previewsByTemplate[name]) || []
      const languages = group.messages.map(m => m.language)
      return rows.filter(row => row.turn_template_data && MEDIA_FORMATS[row.turn_template_data.HEADER_FORMAT] && languages.indexOf(row.lang) !== -1)
    },

    mediaPickerFor (group) {
      return this.mediaPickers[group.key] || { open: false, loading: false, error: '', info: null, selected: null, languageId: null, languageName: '' }
    },

    // Whether the picker is open for this target: a language row, or null for all languages.
    isPickerOpenFor (group, row) {
      const picker = this.mediaPickerFor(group)
      return picker.open && (picker.languageId || null) === (row ? row.language_id : null)
    },

    // One picker per group; it changes either every language (row null) or one language.
    async toggleMediaPicker (group, row = null) {
      const current = this.mediaPickerFor(group)
      if (this.isPickerOpenFor(group, row)) {
        this.$set(this.mediaPickers, group.key, { ...current, open: false })
        return
      }
      const languageId = row ? row.language_id : null
      this.$set(this.mediaPickers, group.key, { open: true, loading: true, error: '', info: null, selected: null, languageId, languageName: row ? row.language : '' })
      const picker = this.mediaPickers[group.key]
      try {
        const { data, status } = await api.buildRequest('nudge-template-media-options', 'POST', { templateId: group.templateId, countryId: this.raw.country_id, ...(languageId ? { languageId } : {}) })
        const payload = (data && data.payload) || {}
        if (status !== 200) throw new Error(payload.message || `HTTP ${status}`)
        picker.info = payload
        picker.selected = (payload.currentMediaIds || [])[0] || null
      } catch (err) {
        picker.error = `Couldn't load Media Management: ${err.message}`
      } finally {
        picker.loading = false
      }
    },

    async applyMedia (group) {
      const picker = this.mediaPickers[group.key]
      const option = picker.info.options.find(o => o.id === picker.selected)
      if (!option) return
      const campaigns = picker.info.campaigns
      const which = picker.languageName ? `${picker.languageName} ${picker.info.label.toLowerCase()}` : `${picker.info.label.toLowerCase()} for every language`
      const confirmed = await this.$swal({
        type: 'warning',
        title: `Use "${option.name}"?`,
        text: `This changes the ${which} on the template "${this.templateName(group.templateId)}" right away, for ${campaigns} campaign${campaigns === 1 ? '' : 's'} using it — not only this one.`,
        showCancelButton: true,
        confirmButtonText: 'Change it'
      })
      if (!confirmed || !confirmed.value) return
      this.saving = true
      try {
        const { data, status } = await api.buildRequest('nudge-template-media', 'POST', { templateId: group.templateId, mediaId: option.id, countryId: this.raw.country_id, ...(picker.languageId ? { languageId: picker.languageId } : {}) })
        const payload = (data && data.payload) || {}
        if (status !== 200) throw new Error(payload.message || `HTTP ${status}`)
        this.$set(this.mediaPickers, group.key, { ...picker, open: false })
        await this.loadTemplatePreview(group, true)
        this.$swal({ type: 'success', text: `${picker.languageName ? `${picker.languageName} header` : 'Header'} changed to "${payload.mediaName}".` })
      } catch (err) {
        this.$swal({ type: 'error', text: `Couldn't change the media: ${err.message}` })
      } finally {
        this.saving = false
      }
    },

    // The stored name, unlike templateName(), which returns display text for a missing one.
    rawTemplateName (templateId) {
      if (!templateId) return ''
      const match = this.templates.find(t => t.id === templateId)
      return match ? match.msg_template_name : ''
    },

    // The approved-template row for one language, or null when there's no template, the fetch
    // hasn't landed, or the template has no approved version in this language.
    templateRowFor (group, message) {
      const name = this.rawTemplateName(group.templateId)
      if (!name) return null
      const rows = this.previewsByTemplate[name]
      if (!rows) return null
      return rows.find(row => row.lang === message.language && row.turn_template_data) || null
    },

    // Header/body split into segments with their stored variable values filled in.
    // dynamic_value_configuration is keyed by language alias and holds { header: [], body: [] }
    // — the same shape createOrUpdateOpportunity.vue writes.
    previewFor (group, message) {
      const row = this.templateRowFor(group, message)
      if (!row) return null
      const data = row.turn_template_data || {}
      const format = data.HEADER_FORMAT
      return {
        // A media header renders the file actually set on the template for this language.
        media: MEDIA_FORMATS[format] ? {
          format,
          mediaId: row.media_id || null,
          mediaName: row.media_name || '',
          fileName: row.media_file_name || '',
          languageId: row.language_id
        } : null,
        header: data.HEADER ? segmentsFor(data.HEADER, this.previewValues(group, message, 'header')) : null,
        body: segmentsFor(String(data.BODY || '').trim(), this.previewValues(group, message, 'body')),
        footer: data.FOOTER || ''
      }
    },

    previewButtons (group, message) {
      const row = this.templateRowFor(group, message)
      const buttons = row && row.turn_template_data && row.turn_template_data.BUTTONS
      return Array.isArray(buttons) ? buttons : []
    },

    // Says which of the two sources the bubble is rendering. Without this, editing the
    // textarea and watching a template-backed bubble not change reads as a broken preview
    // rather than as the point.
    previewNote (group, message) {
      const name = this.rawTemplateName(group.templateId)
      if (!name) {
        return 'No approved template on this group, so this renders the message text on the left. WhatsApp needs an approved template to open a conversation out of session.'
      }
      if (this.previewErrors[name]) {
        return `Couldn't reach Turn to load "${name}", so there's nothing to render. The message text on the left is unaffected.`
      }
      const rows = this.previewsByTemplate[name]
      if (!rows) return 'Loading the approved template…'
      if (!this.templateRowFor(group, message)) {
        // Two different causes, and saying the wrong one sends someone to fix the wrong
        // thing: a language genuinely missing from the template, versus Turn answering
        // without the template body for any language at all.
        if (!rows.some(row => row.turn_template_data)) {
          return `Turn returned no approved body for "${name}" in any language, so there's nothing to render. That's a template-approval or Turn-account problem, not something to fix here.`
        }
        return `"${name}" has no approved version in ${this.languageName(message.language)}, so nothing would go out on WhatsApp for this language.`
      }
      return `Approved template "${name}" — this is what WhatsApp sends. The text on the left is what's stored on the campaign, and is what non-template channels use.`
    },

    async load () {
      this.loading = true
      try {
        const { data, status } = await api.buildRequest('getOpportunityDetails', 'POST', { id: this.campaignId })
        if (status !== 200) throw new Error('Request failed')
        const payload = (data && data.payload) || {}
        if (!payload.id) throw new Error(`No campaign with id ${this.campaignId}.`)
        this.raw = payload

        // Keywords are scoped to the campaign's own country — the unscoped list is long
        // enough that picking the wrong country's keyword is a real mistake to make.
        // All four are lookups for display or for the pickers — a failure in any one of them
        // costs a label or a dropdown, never the page, so each swallows its own error.
        await Promise.all([
          this.loadKeywords(payload.country_id),
          this.loadConversations(),
          this.loadTemplates(),
          this.loadCountriesAndCustomers()
        ])

        this.hydrate(payload)
        // Not awaited: the page is usable before Turn answers; the variable editors and the
        // media panel appear when it does.
        this.form.groups.forEach(group => this.loadTemplatePreview(group))
      } catch (err) {
        this.loadError = err.message
      } finally {
        this.loading = false
      }
    },

    async loadKeywords (countryId) {
      try {
        const { data } = await api.buildRequest('getAllKeywords', 'POST', { countryId })
        this.keywordList = (data && data.payload) || []
      } catch (err) {
        this.keywordList = []
      }
    },

    async loadConversations () {
      try {
        const { data } = await api.buildRequest('getDeployedApps', 'GET', { type: 'CONVERSATION' })
        this.conversationList = (data && data.payload) || []
      } catch (err) {
        this.conversationList = []
      }
    },

    async loadCountriesAndCustomers () {
      try {
        const { data } = await api.buildRequest('getSupportedCountriesLanguages', 'POST')
        this.countries = (data && data.payload) || []
      } catch (err) {
        this.countries = []
      }
      try {
        const { data } = await api.buildRequest('getCustomerList', 'POST')
        this.customers = (data && data.payload && data.payload.customer) || []
      } catch (err) {
        this.customers = []
      }
    },

    async loadTemplates () {
      try {
        const { data } = await api.buildRequest('reengagement/nudges', 'POST')
        this.templates = (data && data.payload) || []
      } catch (err) {
        this.templates = []
      }
    },

    // Turns the stored columns back into the form's own shape. Every reversal here has a
    // matching forward direction in buildPayload() — if one changes, so must the other.
    hydrate (campaign) {
      this.form.name = campaign.opportunity_name || ''

      const timing = campaign.lower_inactivity_timing
      if (campaign.is_in_session && timing) {
        const [hours, minutes] = String(timing).split(':')
        this.form.sendHours = String(parseInt(hours, 10) || 0)
        this.form.sendMinutes = String(parseInt(minutes, 10) || 0)
      } else if (timing) {
        // `${units.charAt(0)}:${quantity}` — 'd:7' is 7 days, 'h:48' is 48 hours.
        const [unit, quantity] = String(timing).split(':')
        this.form.windowUnit = unit === 'h' ? 'hrs' : 'days'
        // Not `|| ''`: 0 is a real stored value ('h:0', which describeTrigger renders as
        // "Immediately on inactivity"), and coercing it to blank would force the programme
        // manager to invent a window before they could save an unrelated change.
        const amount = parseInt(quantity, 10)
        this.form.windowValue = Number.isFinite(amount) ? String(amount) : ''
      }

      this.form.maxNudges = campaign.max_nudge_per_user == null ? '' : String(campaign.max_nudge_per_user)
      this.form.cooldownDays = campaign.last_reengage_days == null ? '' : String(campaign.last_reengage_days)

      if (campaign.start_date) {
        const when = new Date(campaign.start_date)
        if (!Number.isNaN(when.getTime())) {
          // Local getters on purpose: the stored value is an instant, and the inputs show it
          // as the viewer's own wall-clock time (see the note under the date fields).
          this.form.deployDate = isoOf(when)
          this.form.deployTime = `${pad(when.getHours())}:${pad(when.getMinutes())}`
        }
      }

      this.form.groups = (campaign.group_list || []).map((group, i) => ({
        key: group.id || `g${i}`,
        id: group.id,
        // Preserved, not renumbered: updateOpportunity upserts groups on
        // (opportunity_id, group_name), so renaming one on save would leave the original
        // row behind and create a second alongside it.
        groupName: group.group_name || `Group ${i + 1}`,
        splitPercent: group.split_percent == null ? 100 : group.split_percent,
        templateId: group.msg_template_id || null,
        isIntermediate: Boolean(group.is_intermediate_message),
        dynamicValueConfiguration: group.dynamic_value_configuration || null,
        // Per-language variable editors, filled in by initVariables() once the template loads.
        variables: {},
        messages: (group.message || []).map(message => ({
          lang_id: message.lang_id,
          country_id: message.country_id,
          language: message.language,
          message: message.message || ''
        })),
        destinations: (group.destinationList || []).map(row => ({
          type: destinationTypeOf(row),
          keywordId: row.keyword_id || null,
          conversationId: row.destination_conversation_id || null,
          websiteLink: row.website_link || null
        }))
      }))

      // Seed every open/closed key up front so toggling is a plain assignment. The first
      // language of each group starts open — a page that opens fully collapsed reads as
      // empty, and one that opens fully expanded buries the rail on a six-language campaign.
      const expanded = {}
      this.form.groups.forEach(group => {
        group.messages.forEach((message, i) => { expanded[this.langKey(group, message)] = i === 0 })
        // A group with no stored destination still needs a row to fill in — the schema
        // requires one before the campaign can run.
        if (!group.destinations.length) this.addDestination(group)
      })
      this.expanded = expanded
    },

    // Same rules the /opportunity form enforces, restated in nudge language. Collected as a
    // list rather than bailing on the first one: fixing four fields one save at a time is
    // the thing that makes a form feel hostile.
    validate () {
      const errors = []
      const flagged = {}
      if (!this.form.name.trim()) errors.push('The campaign needs a name.')

      if (this.isBatch) {
        if (!this.form.deployDate || !this.form.deployTime) {
          errors.push('A one-time batch needs a deployment date and time.')
        }
      } else if (this.raw.is_in_session) {
        const hours = Number(this.form.sendHours)
        const minutes = Number(this.form.sendMinutes)
        if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
          errors.push('The inactivity window must be a number of hours and minutes.')
        } else if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
          errors.push('The inactivity window must be between 0:00 and 23:59.')
        } else if (hours === 23 && minutes > 0) {
          errors.push('An in-session window can be at most 23 hours.')
        }
      } else {
        const value = Number(this.form.windowValue)
        // 0 is allowed through deliberately — see the hydrate() note. Above zero, the
        // /opportunity form's own floors apply.
        if (this.form.windowValue === '' || !Number.isFinite(value) || value < 0) {
          errors.push('The inactivity window needs a value.')
        } else if (value > 0 && this.form.windowUnit === 'hrs' && value < 24) {
          errors.push('An out-of-session window must be at least 24 hours — use days below that.')
        }
      }

      if (this.form.groups.length > 1) {
        const total = this.form.groups.reduce((sum, group) => sum + (Number(group.splitPercent) || 0), 0)
        if (total !== 100) errors.push(`The A/B split has to add up to 100% — it currently adds up to ${total}%.`)
      }

      this.form.groups.forEach((group, i) => {
        const label = this.variantLabel(i)
        if (group.messages.length && !group.messages.some(m => (m.message || '').trim())) {
          errors.push(`${label} has no message text in any language.`)
        }
        group.messages.forEach(message => {
          if (this.lengthOf(message) > MAX_BODY_CHARS) {
            flagged[message.lang_id] = true
            // Force it open: an over-length body hidden inside a collapsed block is a fix
            // you can't find.
            this.expanded[this.langKey(group, message)] = true
            errors.push(`${label} — the ${this.languageName(message.language)} message is ${this.lengthOf(message)} characters; the limit is ${MAX_BODY_CHARS}.`)
          }
        })
        // The template's slots come from Turn. Until that answers there's nothing to check
        // the values against, so saving waits rather than skipping the check silently.
        const templateName = this.rawTemplateName(group.templateId)
        if (templateName && this.previewsByTemplate[templateName] === undefined) {
          errors.push(`${label}: still loading the template "${templateName}" from Turn — try again in a moment.`)
        }

        // Template values: every slot filled, fixed text within Meta's parameter rules, and
        // the message as sent within WhatsApp's limits. Only for Dynamic templates — the
        // sender ignores values otherwise (templateWarnings says so).
        group.messages.forEach(message => {
          const vars = group.variables[message.language]
          if (!vars || !vars.dynamic) return
          const row = this.templateRowFor(group, message)
          const language = this.languageName(message.language)
          const problems = []
          this.slotsFor(group, message).forEach(slot => {
            if (!slot.entry.source) problems.push(`${slot.label} has no value`)
            else if (slot.entry.source === 'text') {
              const problem = paramProblem(slot.entry.text)
              if (problem) problems.push(`${slot.label} text ${problem}`)
            }
          })
          if (!problems.length && row) {
            const data = row.turn_template_data || {}
            const filled = (text, entries) => {
              let i = 0
              return String(text || '').replace(TEMPLATE_VARIABLE, token => {
                const entry = entries[slotIndex(token, i++)]
                return entry && entry.source === 'text' ? entry.text.trim() : 'x'.repeat(TOKEN_LENGTH_BUDGET)
              }).length
            }
            const body = filled(String(data.BODY || '').trim(), vars.body)
            if (body > MAX_BODY_CHARS) problems.push(`the message would be ${body} characters with its values in (limit ${MAX_BODY_CHARS})`)
            if (vars.header.length) {
              const header = filled(data.HEADER, vars.header)
              if (header > MAX_HEADER_CHARS) problems.push(`the header would be ${header} characters with its value in (limit ${MAX_HEADER_CHARS})`)
            }
          }
          if (problems.length) {
            flagged[message.lang_id] = true
            this.expanded[this.langKey(group, message)] = true
            errors.push(`${label} — ${language}: ${problems.join('; ')}.`)
          }
        })

        const resolved = group.destinations.filter(this.destinationIsSet)
        if (!resolved.length) errors.push(`${label} needs a destination — a keyword or a conversation.`)
      })

      this.errors = errors
      this.flagged = flagged
      return !errors.length
    },

    destinationIsSet (destination) {
      if (destination.type === 'website_link') return Boolean(destination.websiteLink)
      if (destination.type === 'keyword') return Boolean(destination.keywordId)
      return Boolean(destination.conversationId)
    },

    // updateOpportunity spreads this straight into the update, so it has to carry every
    // column — hence building on `raw` rather than assembling a fresh object. A column this
    // form has never heard of survives a save untouched.
    buildPayload (activate) {
      const payload = { ...this.raw }
      // group_list is rebuilt below from the form; `created` is dropped because re-sending
      // it on an update would rewrite the campaign's own creation date (the /opportunity
      // form deletes it for the same reason).
      delete payload.group_list
      delete payload.created

      payload.id = this.campaignId
      payload.opportunity_name = this.form.name.trim()
      payload.updated = new Date()

      // opportunity_status is the only column updateOpportunityStatus touches for the
      // Dashboard's Activate toggle — activity_status is deliberately left alone here too, so
      // both paths mean exactly the same thing by "active".
      if (activate) payload.opportunity_status = CAMPAIGN_STATUS.active

      if (this.isBatch) {
        const [year, month, day] = this.form.deployDate.split('-').map(Number)
        const [hours, minutes] = this.form.deployTime.split(':').map(Number)
        payload.start_date = new Date(year, month - 1, day, hours, minutes, 0, 0).toISOString()
      } else if (this.raw.is_in_session) {
        payload.lower_inactivity_timing = `${pad(Number(this.form.sendHours) || 0)}:${pad(Number(this.form.sendMinutes) || 0)}`
      } else {
        payload.lower_inactivity_timing = `${this.form.windowUnit.charAt(0)}:${Number(this.form.windowValue)}`
        payload.max_nudge_per_user = this.form.maxNudges === '' ? null : Number(this.form.maxNudges)
        payload.last_reengage_days = this.form.cooldownDays === '' ? null : Number(this.form.cooldownDays)
      }

      // 'MANUAL'/'EQUAL' are the only values splitOptions defines — 'CUSTOM' isn't one, and
      // sending it is what used to 400 every A/B write.
      payload.split_type = this.form.groups.length > 1 ? 'MANUAL' : 'EQUAL'

      payload.group_list = this.form.groups.map((group, i) => ({
        group_name: group.groupName || `Group ${i + 1}`,
        msg_template_id: group.templateId,
        split_percent: this.form.groups.length > 1 ? Number(group.splitPercent) : 100,
        dynamic_value_configuration: this.dynamicConfigFor(group),
        is_intermediate_message: group.isIntermediate,
        messages: group.messages
          .filter(message => (message.message || '').trim())
          .map(message => ({
            lang_id: message.lang_id,
            country_id: message.country_id,
            message: message.message
          })),
        options: group.destinations.filter(this.destinationIsSet).map(destination => ({
          link_id: destination.type === 'website_link' ? destination.websiteLink : null,
          keyword_id: destination.type === 'keyword' ? destination.keywordId : null,
          destination_conversation_id: destination.type === 'destination_conversation' ? destination.conversationId : null
        }))
      }))

      return payload
    },

    async save (activate = false) {
      if (!this.validate()) {
        this.$swal({ type: 'warning', text: 'Some fields still need attention — see the list above the content.' })
        return
      }

      this.saving = true
      try {
        const { data, status } = await api.buildRequest('updateOpportunity', 'POST', this.buildPayload(activate))
        if (status !== 200) throw new Error('Request failed')
        const payload = (data && data.payload) || {}
        // updateOpportunity answers a name clash with a 200 and a message rather than an
        // error status, so the only way to notice is to read the body.
        if (payload.message === 'Campaign already exists') {
          this.errors = ['Another campaign already uses that name.']
          this.$swal({ type: 'warning', text: 'Another campaign already uses that name — pick a different one.' })
          return
        }
        this.$swal({ type: 'success', text: activate ? 'Campaign updated and activated.' : 'Campaign updated.' })
        // `opportunityId` is what NudgeDashboard.vue's own isHighlighted() reads.
        this.$router.push({ path: '/nudge-console/dashboard', query: { opportunityId: this.campaignId } })
      } catch (err) {
        this.$swal({ type: 'error', text: `Couldn't save the campaign: ${err.message}` })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped src="./nudge-console-theme.css"></style>
<style scoped src="./EditCampaign.css"></style>



// WEBPACK FOOTER //
// EditCampaign.vue?41eb38de